import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { formatMoney, pickLocale } from '../lib/format';
import { BrandMark } from '../components/BrandMark';
import { ConsentBanner } from '../components/ConsentBanner';
import { startAnalytics, pageview } from '../lib/analytics';

type I18nText = Record<string, string>;
interface Hour { days: number[]; start: string; end: string }
interface Rest {
  id: string; name: string; slug: string; locale: string; currency: string; cuisine_category: string | null;
  city: string | null; state: string | null;
  public_description: string | null; public_address: string | null; public_maps_url: string | null;
  public_phone: string | null; public_hours: Hour[]; public_tags: string[]; public_template: string; public_show_prices: boolean;
}
interface Cat { id: string; name: I18nText }
interface Item { id: string; category_id: string | null; name: I18nText; price_cents: number }

export function PublicRestaurantPage() {
  const { country, state, city, slug } = useParams();
  const { i18n } = useTranslation();
  const [rest, setRest] = useState<Rest | null>(null);
  const [cats, setCats] = useState<Cat[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.from('restaurants')
        .select('id, name, slug, locale, currency, cuisine_category, city, state, public_description, public_address, public_maps_url, public_phone, public_hours, public_tags, public_template, public_show_prices')
        .eq('country_slug', country).eq('state_slug', state).eq('city_slug', city).eq('slug', slug).eq('public_published', true).maybeSingle();
      if (data) {
        const r = data as unknown as Rest;
        setRest(r);
        const { data: c } = await supabase.from('menu_categories').select('id, name').eq('restaurant_id', r.id).eq('is_active', true).order('position');
        const { data: it } = await supabase.from('menu_items').select('id, category_id, name, price_cents').eq('restaurant_id', r.id).eq('is_available', true).order('position');
        if (c) setCats(c as Cat[]);
        if (it) setItems(it as Item[]);
      }
      setDone(true);
    })();
  }, [country, state, city, slug]);

  useEffect(() => {
    if (!rest) return;
    const where = [rest.city, rest.state].filter(Boolean).join(', ');
    document.title = `${rest.name}${where ? ' — ' + where : ''} | La petite table`;
    const ld = {
      '@context': 'https://schema.org', '@type': 'Restaurant', name: rest.name,
      servesCuisine: rest.cuisine_category || undefined,
      address: { '@type': 'PostalAddress', streetAddress: rest.public_address || undefined, addressLocality: rest.city || undefined, addressRegion: rest.state || undefined },
      telephone: rest.public_phone || undefined,
      description: rest.public_description || undefined
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(ld);
    document.head.appendChild(el);
    startAnalytics();
    pageview(window.location.pathname, document.title);
    return () => { document.head.removeChild(el); };
  }, [rest]);

  if (!done) return <div className="center-note">…</div>;
  if (!rest) return <div className="center-note">{i18n.getFixedT('pt-BR')('pub.not_found')}</div>;

  const tt = i18n.getFixedT(rest.locale || 'pt-BR');
  const locale = rest.locale || 'pt-BR';

  return (
    <div className={`pub-page pub-tpl--${rest.public_template}`}>
      <header className="pub-hero">
        <h1 className="pub-hero__name">{rest.name}</h1>
        <p className="pub-hero__meta">
          {[rest.cuisine_category, [rest.city, rest.state].filter(Boolean).join(' · ')].filter(Boolean).join(' · ')}
        </p>
        {rest.public_description && <p className="pub-hero__desc">{rest.public_description}</p>}
        {rest.public_tags.length > 0 && (
          <div className="pub-tags">
            {rest.public_tags.map((tag) => <span key={tag} className="pub-tag">{tt(`pub.tag_${tag}`)}</span>)}
          </div>
        )}
      </header>

      <section className="pub-info">
        {(rest.public_address || rest.public_maps_url) && (
          <div className="pub-info__block">
            <h3>{tt('pub.address')}</h3>
            {rest.public_address && <p>{rest.public_address}</p>}
            {rest.public_maps_url && <a href={rest.public_maps_url} target="_blank" rel="noreferrer">{tt('pub.directions')} →</a>}
          </div>
        )}
        {rest.public_hours.length > 0 && (
          <div className="pub-info__block">
            <h3>{tt('pub.hours')}</h3>
            {rest.public_hours.filter((h) => h.start && h.end && (h.days ?? []).length).map((h, i) => (
              <p key={i}><strong>{h.days.map((d) => tt(`menu.dow_${d}`)).join(', ')}</strong> {h.start.slice(0, 5)}–{h.end.slice(0, 5)}</p>
            ))}
          </div>
        )}
        {rest.public_phone && (
          <div className="pub-info__block">
            <h3>{tt('pub.reservation')}</h3>
            <p><a href={`tel:${rest.public_phone}`}>{rest.public_phone}</a></p>
          </div>
        )}
      </section>

      <section className="pub-menu">
        <h2 className="pub-section-title">{tt('pub.menu')}</h2>
        {cats.map((c) => {
          const list = items.filter((i) => i.category_id === c.id);
          if (list.length === 0) return null;
          return (
            <div key={c.id} className="pub-menu__cat">
              <h3>{pickLocale(c.name, locale)}</h3>
              {list.map((it) => (
                <div key={it.id} className="pub-menu__line">
                  <span>{pickLocale(it.name, locale)}</span>
                  {rest.public_show_prices && <span className="pub-menu__price">{formatMoney(it.price_cents, rest.currency, locale)}</span>}
                </div>
              ))}
            </div>
          );
        })}
      </section>

      <footer className="pub-footer">
        <a className="btn btn--primary" href={`/fila/${rest.slug}`}>{tt('pub.join_queue')}</a>
        <span className="pub-footer__brand"><BrandMark className="brand__mark" /> La petite table</span>
      </footer>
      <ConsentBanner />
    </div>
  );
}
